import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useGetBrokerDetails as useGetSpecificBrokerDetails } from "../../../api/queries/societies.queries";
import { useGetBrokerDetails as useGetCurrentUserBrokerDetails } from "../../../api/queries/users.queries";
import { howDidYouKnowDune, positionsInTheCompany } from "@dune/types";

const categorySchema = z.object({
	category: z.string(),
	status: z.enum(["REGISTERED", "NOT_REGISTERED"]),
	registrationDate: z.string(),
	categoryDeletionDate: z.string().optional(),
	fundCollection: z.boolean(),
	mandants: z.string().optional(),
});

const statusForCategory = ["REGISTERED", "NOT_REGISTERED"] as const;
export type StatusForCategory = (typeof statusForCategory)[number];

export const registerSecondStepDetailsFormSchema = z
	.object({
		lastname: z.string().min(1, "Le nom est requis"),
		firstname: z.string().min(1, "Le prénom est requis"),
		email: z.email("Email invalide").min(1, "L'email est requis"),
		position: z.enum(positionsInTheCompany, {
			error: "La fonction est requise",
		}),
		landlinePhone: z
			.string()
			.transform((val) => (val === "" ? undefined : val))
			.optional()
			.refine((val) => val === undefined || /^[+]?[\d\s()-]*$/.test(val), {
				message: "Numéro de téléphone fixe invalide",
			}),
		phone: z.string().min(1, "Le mobile est requis"),
		isSignatory: z.boolean(),

		lastnameSignatory: z.string().optional(),
		firstnameSignatory: z.string().optional(),
		emailSignatory: z
			.string()
			.transform((val) => (val === "" ? undefined : val))
			.optional()
			.refine((val) => val === undefined || z.email().safeParse(val).success, {
				message: "Email du signataire invalide",
			}),
		positionSignatory: z.enum(positionsInTheCompany).optional(),
		landlinePhoneSignatory: z
			.string()
			.transform((val) => (val === "" ? undefined : val))
			.optional()
			.refine((val) => val === undefined || /^[+]?[\d\s()-]*$/.test(val), {
				message: "Numéro de téléphone fixe du signataire invalide",
			}),
		phoneSignatory: z
			.string()
			.transform((val) => (val === "" ? undefined : val))
			.optional()
			.refine((val) => val === undefined || /^[+]?[\d\s()-]*$/.test(val), {
				message: "Numéro de téléphone du signataire invalide",
			}),

		oriasNumber: z.string().optional(),
		siren: z.string().optional(),
		denomination: z.string().optional(),
		headquartersAddress: z.string().optional(),
		categories: z.array(categorySchema).optional(),

		newsletter: z.boolean().optional(),
		howDidYouKnowDune: z.enum(howDidYouKnowDune, {
			error: "Ce champ est requis",
		}),
	})
	.superRefine((data, ctx) => {
		if (!data.isSignatory) {
			if (!data.lastnameSignatory) {
				ctx.addIssue({
					code: "custom",
					message: "Le nom du signataire est requis",
					path: ["lastnameSignatory"],
				});
			}
			if (!data.firstnameSignatory) {
				ctx.addIssue({
					code: "custom",
					message: "Le prénom du signataire est requis",
					path: ["firstnameSignatory"],
				});
			}
			if (!data.emailSignatory) {
				ctx.addIssue({
					code: "custom",
					message: "L'email du signataire est requis",
					path: ["emailSignatory"],
				});
			}
			if (!data.positionSignatory) {
				ctx.addIssue({
					code: "custom",
					message: "La fonction du signataire est requise",
					path: ["positionSignatory"],
				});
			}
			if (!data.phoneSignatory) {
				ctx.addIssue({
					code: "custom",
					message: "Le téléphone du signataire est requis",
					path: ["phoneSignatory"],
				});
			}
		}
	});

export type RegisterSecondStepDetailsFormData = z.infer<
	typeof registerSecondStepDetailsFormSchema
>;

export function useRegisterSecondStepDetailsForm(brokerId?: string) {
	const form = useForm<RegisterSecondStepDetailsFormData>({
		defaultValues: {
			oriasNumber: "",
			siren: "",
			denomination: "",
			headquartersAddress: "",
			lastname: "",
			firstname: "",
			email: "",
			position: undefined,
			landlinePhone: "",
			phone: "",
			isSignatory: true,
			newsletter: false,
			howDidYouKnowDune: undefined,
			categories: [],
		},
		resolver: zodResolver(registerSecondStepDetailsFormSchema),
	});

	const { data: currentUserBrokerData, isLoading: isLoadingCurrentUser } =
		useGetCurrentUserBrokerDetails();
	const { data: specificBrokerData, isLoading: isLoadingSpecificBroker } =
		useGetSpecificBrokerDetails(brokerId);

	const brokerData = brokerId ? specificBrokerData : currentUserBrokerData;
	const isLoading = brokerId ? isLoadingSpecificBroker : isLoadingCurrentUser;
	useEffect(() => {
		if (!brokerData || !brokerData.categoriesOnSociety) return;

		const categories = brokerData.categoriesOnSociety.map(
			(catOnSociety: {
				category: {
					category: string;
					status: StatusForCategory;
					registrationDate: Date | null;
					categoryDeletionDate?: Date | null;
					fundCollection: boolean;
				};
			}) => ({
				category: catOnSociety.category.category,
				status: catOnSociety.category.status,
				registrationDate: catOnSociety.category.registrationDate
					? new Date(catOnSociety.category.registrationDate).toISOString()
					: "",
				categoryDeletionDate: catOnSociety.category.categoryDeletionDate
					? new Date(catOnSociety.category.categoryDeletionDate).toISOString()
					: undefined,
				fundCollection: catOnSociety.category.fundCollection,
				mandants: "",
			}),
		);

		const mainBrokerUser = brokerData.users[0];

		form.reset({
			oriasNumber: brokerData.oriasNumber?.toString() || "",
			siren: brokerData.siren || "",
			denomination: brokerData.denomination || "",
			headquartersAddress: brokerData.headOfficeAddress
				? `${brokerData.headOfficeAddress.streetNumber} ${brokerData.headOfficeAddress.streetName}, ${brokerData.headOfficeAddress.postalCode}, ${brokerData.headOfficeAddress.city}`
				: "",
			lastname: mainBrokerUser.lastname || "",
			firstname: mainBrokerUser.firstname || "",
			email: mainBrokerUser.email || "",
			position: mainBrokerUser.positionInTheCompany || undefined,
			landlinePhone: mainBrokerUser.landlinePhone || "",
			phone: mainBrokerUser.mobilePhone || "",
			isSignatory: mainBrokerUser.isSignatory ?? true,
			newsletter: mainBrokerUser.hasSubscribedToNewsletter ?? false,
			howDidYouKnowDune: mainBrokerUser.howDidYouKnowDune || undefined,
			categories,
		});
	}, [brokerData, form]);

	return { form, isLoading };
}
